import React, {useCallback, useEffect, useMemo} from 'react';
import {goCS, toPriceStr} from '@/utils';
import {useTranslation} from 'react-i18next';
import RechargeRule from './recharge-rule';
import type {RechargeProgressiveVm} from './use-recharge-progressive-core';
import {OpenDiskSection} from './recharge-open-disk-section';
import {
  RechargeWebNavFrame,
  RechargeWebBalanceCard,
  RechargeWebChannelList,
  RechargeWebAmountMinMaxHint,
  resolveRechargeWebAmountMinMaxLabels,
  resolveRechargeWebOpenDiskWeekTopLabels,
  RechargeWebRechargeSubmitBar,
} from '@fe-progressive/package-progressive/recharge-web';
import {RECHARGE_PROGRESSIVE_SUBMIT_BUTTON_BACKGROUND} from './recharge-progressive.constants';
import {normalizeProgressivePackageLocale} from '@fe-progressive/package-progressive/locale';
import {mapWeekCardListToRechargeAmountGridRows} from '@fe-progressive/package-progressive/recharge';
import {ProgressiveLoadingOverlay} from '@fe-progressive/package-progressive/progressive-loading';
import {getRechargeWebBalanceHeaderBgSrc} from './util/recharge-web-balance-bg';
import {
  getRechargeWebBalanceCardHeightRem,
  shouldShowRechargeWebBalanceMethodSection,
} from './util/recharge-web-balance-layout';
import type {BalanceListItem} from './recharge.service';

export type RechargeCoreShellProps = {
  vm: RechargeProgressiveVm;
  onBack: () => void;
};

const RECHARGE_VIEWPORT_HEIGHT_VAR = '--recharge-viewport-height';

function useRechargeViewportHeightCssVar() {
  useEffect(() => {
    const updateViewportHeight = () => {
      const height = window.visualViewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty(
        RECHARGE_VIEWPORT_HEIGHT_VAR,
        `${height}px`,
      );
    };

    updateViewportHeight();
    window.addEventListener('resize', updateViewportHeight);
    window.visualViewport?.addEventListener('resize', updateViewportHeight);
    window.visualViewport?.addEventListener('scroll', updateViewportHeight);

    return () => {
      window.removeEventListener('resize', updateViewportHeight);
      window.visualViewport?.removeEventListener(
        'resize',
        updateViewportHeight,
      );
      window.visualViewport?.removeEventListener(
        'scroll',
        updateViewportHeight,
      );
      document.documentElement.style.removeProperty(
        RECHARGE_VIEWPORT_HEIGHT_VAR,
      );
    };
  }, []);
}

export const RechargeCoreShell: React.FC<RechargeCoreShellProps> = ({
  vm,
  onBack,
}) => {
  useRechargeViewportHeightCssVar();

  const {t} = useTranslation();
  const {
    loading,
    isWeekCard,
    openDiskPayType,
    setOpenDiskPayType,
    openDiskPresetValue,
    setOpenDiskPresetValue,
    payConfig,
    paymethodList,
    payMethodId,
    setPayMethodId,
    payMethodItem,
    balance,
    setBalance,
    amount,
    displayBalance,
    canShowBalanceList,
    extraBounsBalanceIndex,
    activeTab,
    balanceConfig,
    useType,
    weekCardList,
    setActiveWeekCardID,
    handleRefresh,
    handleRecharge,
    handleGotoRecords,
    truncateToDecimalPlaces,
    calculateTotalAmount,
    enableCrypto,
    core,
    i18n: vmI18n,
    currencySymbol,
  } = vm;

  const progressiveLocale = useMemo(
    () => normalizeProgressivePackageLocale(vmI18n.language),
    [vmI18n.language],
  );

  const balanceItem: BalanceListItem | undefined = useMemo(
    () => canShowBalanceList[extraBounsBalanceIndex],
    [canShowBalanceList, extraBounsBalanceIndex],
  );

  const amountGridRows = useMemo(() => {
    if (isWeekCard) {
      return mapWeekCardListToRechargeAmountGridRows(weekCardList);
    }
    return canShowBalanceList;
  }, [isWeekCard, weekCardList, canShowBalanceList]);

  const onPanelAmountChange = useCallback(
    (v: string) => {
      if (isWeekCard) {
        const card = weekCardList.find(
          c => String(c.price ?? '') === v || String(c.price) === v,
        );
        if (card?.cardId != null) {
          setActiveWeekCardID(card.cardId);
        }
        return;
      }
      setBalance(v);
    },
    [isWeekCard, weekCardList, setActiveWeekCardID, setBalance],
  );

  const minMaxLabels = useMemo(
    () => resolveRechargeWebAmountMinMaxLabels(progressiveLocale),
    [progressiveLocale],
  );

  const minMaxHintNode = useMemo(() => {
    if (!payMethodItem) {
      return undefined;
    }
    return (
      <RechargeWebAmountMinMaxHint
        minAmount={payMethodItem.minAmount}
        maxAmount={payMethodItem.maxAmount}
        formatAmount={n =>
          toPriceStr(n, {
            fixed: 0,
            thousands: true,
          })
        }
        labels={minMaxLabels}
      />
    );
  }, [minMaxLabels, payMethodItem]);

  const weekTopLabels = useMemo(
    () => resolveRechargeWebOpenDiskWeekTopLabels(progressiveLocale),
    [progressiveLocale],
  );

  const topNoticeNode = useMemo(
    () =>
      isWeekCard ? (
        <span>
          <span className="text-[#F15802] mr-1">*</span>
          {weekTopLabels.buyTip}
        </span>
      ) : undefined,
    [isWeekCard, weekTopLabels.buyTip],
  );

  const submitDisabled = useMemo(
    () =>
      loading ||
      displayBalance === '' ||
      +displayBalance <= 0 ||
      typeof payMethodId === 'undefined' ||
      !payMethodItem,
    [loading, displayBalance, payMethodId, payMethodItem],
  );

  return (
    <div
      className="flex min-h-0 flex-col overflow-hidden bg-[#f5f5f5]"
      style={{height: `var(${RECHARGE_VIEWPORT_HEIGHT_VAR}, 100vh)`}}>
      <div className="shrink-0">
        <RechargeWebNavFrame
          title={
            isWeekCard ? t('vip.label.weekCardRecharge') : t('label.recharge')
          }
          onBack={onBack}
          onCustomerService={goCS}
        />
      </div>
      <div
        className="relative flex min-h-0 flex-1 flex-col overflow-hidden"
        aria-busy={loading}
        aria-live="polite">
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-3 pb-3 pt-3">
          <RechargeWebBalanceCard
            className="shrink-0"
            backgroundImageSrc={getRechargeWebBalanceHeaderBgSrc()}
            locale={progressiveLocale}
            mainAmount={
              <span className="text-2xl font-bold font-din leading-none break-all">
                {toPriceStr(amount, {
                  thousands: true,
                  spacing: true,
                })}
              </span>
            }
            payName={payMethodItem?.payName ?? ''}
            onRefresh={handleRefresh}
            onRecords={handleGotoRecords}
            fromWeekCard={isWeekCard}
            showMethodSection={shouldShowRechargeWebBalanceMethodSection()}
            cardHeightRem={getRechargeWebBalanceCardHeightRem()}
          />

          <OpenDiskSection
            core={core}
            enableCrypto={enableCrypto}
            isWeekCard={isWeekCard}
            openDiskPayType={openDiskPayType}
            onOpenDiskPayTypeChange={setOpenDiskPayType}
            openDiskPresetValue={openDiskPresetValue}
            onOpenDiskPresetValueChange={setOpenDiskPresetValue}
            canShowBalanceList={canShowBalanceList}
            payMethodItem={payMethodItem}
            activeTab={activeTab}
            balanceConfig={balanceConfig}
            payConfig={payConfig}
            useType={useType}
            extraBounsBalanceIndex={extraBounsBalanceIndex}
            truncateToDecimalPlaces={truncateToDecimalPlaces}
            calculateTotalAmount={calculateTotalAmount}
            upiOpenDiskAmount={{
              amountText: balance,
              onAmountChange: onPanelAmountChange,
              balanceList: amountGridRows,
              topNotice: topNoticeNode,
              minMaxHint: minMaxHintNode,
              inputReadOnly: isWeekCard,
              amountGridClassName: isWeekCard ? 'grid-cols-3' : undefined,
              isWeekCard,
            }}
          />

          {openDiskPayType !== 'CRYPTO' ? (
            <RechargeWebChannelList
              locale={progressiveLocale}
              channels={paymethodList}
              selectedId={payMethodId}
              onSelect={setPayMethodId}
              currencySymbol={currencySymbol}
            />
          ) : null}

          {openDiskPayType !== 'CRYPTO' ? (
            <RechargeRule
              fromWeekCard={isWeekCard}
              currencySymbol={currencySymbol}
            />
          ) : null}
        </div>

        {/* 文案色默认白；某盘需黑字时可传 submitButtonTextClassName="text-black" 等 */}
        <RechargeWebRechargeSubmitBar
          locale={progressiveLocale}
          disabled={submitDisabled}
          isWeekCard={isWeekCard}
          displayBalance={displayBalance}
          useType={useType}
          payMethodItem={payMethodItem}
          balanceItem={balanceItem}
          activeTab={activeTab}
          balanceConfig={balanceConfig}
          exchangeRate={payConfig?.exchange}
          cryptoRechargeFee={payConfig?.cryptoRechargeFee}
          submitButtonBackground={RECHARGE_PROGRESSIVE_SUBMIT_BUTTON_BACKGROUND}
          onPress={handleRecharge}
        />

        <ProgressiveLoadingOverlay visible={loading} />
      </div>
    </div>
  );
};
